import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

const ProductPlanForm = ({ plan, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    paddle_plan_id: '',
    billing_interval: '',
    price_info: '',
    trial_days: '',
    beta_end_date: '',
    beta_requires_registration: true,
    beta_max_licenses: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isEditing && plan) {
      // Format beta_end_date for input[type="date"]
      let betaEndDateValue = ''
      if (plan.beta_end_date) {
        const date = new Date(plan.beta_end_date)
        betaEndDateValue = date.toISOString().split('T')[0]
      }
      setFormData({
        name: plan.name || '',
        paddle_plan_id: plan.paddle_plan_id || '',
        billing_interval: plan.billing_interval || '',
        price_info: plan.price_info || '',
        trial_days: plan.trial_days || '',
        beta_end_date: betaEndDateValue,
        beta_requires_registration: plan.beta_requires_registration ?? true,
        beta_max_licenses: plan.beta_max_licenses ?? ''
      })
    }
  }, [isEditing, plan])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    if (error) setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const { name, paddle_plan_id, billing_interval, beta_end_date } = formData
    if (!name || !paddle_plan_id || !billing_interval) {
      setError('Name, Paddle Plan ID und Billing Interval sind erforderlich')
      setIsLoading(false)
      return
    }

    // Validate beta_end_date is provided when billing_interval is "beta"
    if (billing_interval === 'beta' && !beta_end_date) {
      setError('Beta End Date ist erforderlich für Beta Billing Interval')
      setIsLoading(false)
      return
    }

    let trialDaysValue = formData.trial_days
    if (formData.billing_interval === 'trial') {
      trialDaysValue = formData.trial_days ? Number.parseInt(formData.trial_days, 10) : null
    }

    let betaEndDateValue = null
    if (formData.billing_interval === 'beta' && formData.beta_end_date) {
      betaEndDateValue = new Date(formData.beta_end_date).toISOString()
    }

    const { beta_requires_registration, beta_max_licenses, ...baseData } = formData
    const betaData = {}
    if (formData.billing_interval === 'beta') {
      betaData.beta_requires_registration = Boolean(beta_requires_registration)
      betaData.beta_max_licenses = beta_max_licenses === '' || beta_max_licenses == null
        ? null
        : Number.parseInt(beta_max_licenses, 10)
    }

    const submitData = {
      ...baseData,
      ...betaData,
      price_info: formData.billing_interval === 'trial' || formData.billing_interval === 'beta' ? '' : formData.price_info,
      trial_days: formData.billing_interval === 'trial' ? trialDaysValue : null,
      beta_end_date: betaEndDateValue
    }
    onSave(submitData)
    setIsLoading(false)
  }

  return (
    <div className="user-form-overlay">
      <div className="user-form-container">
        <div className="user-form-header">
          <h3>{isEditing ? 'Edit Plan' : 'Add New Plan'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form className="user-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Plan Name" required disabled={isLoading} />
          </div>
          <div className="form-group">
            <label htmlFor="paddle_plan_id">Paddle Plan ID</label>
            <input type="text" id="paddle_plan_id" name="paddle_plan_id" value={formData.paddle_plan_id} onChange={handleChange} placeholder="Plan ID" required disabled={isLoading} />
          </div>
          <div className="form-group">
            <label htmlFor="billing_interval">Billing Interval</label>
            <select id="billing_interval" name="billing_interval" value={formData.billing_interval} onChange={handleChange} required disabled={isLoading}>
              <option value="">Select...</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="one-time">One-Time</option>
              <option value="trial">Trial</option>
              <option value="beta">Beta</option>
            </select>
          </div>
          <div className="form-group">
            {(() => {
              if (formData.billing_interval === 'trial') {
                return (
                  <>
                    <label htmlFor="trial_days">Trial Days</label>
                    <input type="number" id="trial_days" name="trial_days" value={formData.trial_days} onChange={handleChange} placeholder="Number of trial days" min="1" disabled={isLoading} required />
                  </>
                )
              }
              if (formData.billing_interval === 'beta') {
                return (
                  <>
                    <label htmlFor="beta_end_date">Beta End Date</label>
                    <input type="date" id="beta_end_date" name="beta_end_date" value={formData.beta_end_date} onChange={handleChange} disabled={isLoading} required />
                    <label htmlFor="beta_requires_registration">
                      <input type="checkbox" id="beta_requires_registration" name="beta_requires_registration" checked={formData.beta_requires_registration} onChange={handleChange} disabled={isLoading} />
                      {' '}Registration required
                    </label>
                    <label htmlFor="beta_max_licenses">Max. beta testers</label>
                    <input type="number" id="beta_max_licenses" name="beta_max_licenses" value={formData.beta_max_licenses} onChange={handleChange} placeholder="unlimited" min="1" disabled={isLoading} />
                  </>
                )
              }
              return (
                <>
                  <label htmlFor="price_info">Price Info (optional)</label>
                  <input type="text" id="price_info" name="price_info" value={formData.price_info} onChange={handleChange} placeholder="e.g. $9.99" disabled={isLoading} />
                </>
              )
            })()}
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel} disabled={isLoading}>Cancel</button>
            <button type="submit" className="btn-save" disabled={isLoading}>{isEditing ? 'Update' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

ProductPlanForm.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    paddle_plan_id: PropTypes.string,
    billing_interval: PropTypes.string,
    price_info: PropTypes.string,
    trial_days: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    beta_end_date: PropTypes.string,
    beta_requires_registration: PropTypes.bool,
    beta_max_licenses: PropTypes.number
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool
}

export default ProductPlanForm 